/**
 * Shared helpers for ticket navigation.
 * Use navigateToNewTicket() from any page to open the ticket creation form
 * with optional pre-filled fields, instead of building the URL manually.
 */

/**
 * Navigate to the ticket creation form with optional pre-filled parameters.
 *
 * @param {function} navigate  - react-router navigate() function
 * @param {object}   opts
 * @param {string}   [opts.requesterId]  - pre-fill requester (tk_users_ext id)
 * @param {string}   [opts.clientId]     - pre-fill client/société
 * @param {string}   [opts.phone]        - pre-fill phone number
 * @param {string}   [opts.title]        - pre-fill ticket title
 * @param {string}   [opts.source]       - ticket source (phone, email, web…)
 */
export function navigateToNewTicket(navigate, { requesterId, clientId, phone, title, source } = {}) {
  const params = new URLSearchParams();
  if (requesterId) params.set('requester_id', requesterId);
  if (clientId)    params.set('client_id', clientId);
  if (phone)       params.set('phone', phone);
  if (title)       params.set('title', title);
  if (source)      params.set('source', source);
  navigate(`/tickets/new${params.toString() ? `?${params}` : ''}`);
}

/**
 * Reinjecte la categorie courante d'un ticket/tache dans une liste de
 * categories deja filtree aux actives (cf /api/tk/admin/categories?active=true),
 * si elle en est absente.
 *
 * Sans ca, un enregistrement ancien portant une categorie desormais eteinte
 * afficherait un champ vide dans son selecteur — ce qui se lit comme une
 * perte de donnee plutot que comme une categorie simplement retiree du
 * referentiel.
 *
 * @param {Array<{id: string, name: string}>} list  - categories actives deja chargees
 * @param {string} currentId                          - category_id porte par le ticket/la tache
 * @param {string} [currentName]                       - nom connu de cette categorie, si disponible
 * @returns {Array<{id: string, name: string}>}
 */
export function withArchivedCategory(list, currentId, currentName) {
  const options = list || [];
  if (!currentId || options.some(c => c.id === currentId)) return options;
  return [...options, { id: currentId, name: `${currentName || 'Categorie'} (archivee)` }];
}

/**
 * Determine which category_id to keep on a record after its organization
 * selector changes (cf DailyLogSection.js, entries hors-ticket).
 *
 * Categories are organization-scoped server-side (une categorie porte son
 * propre organization_id et le backend rejette toute incoherence a
 * l'ecriture) : une categorie qui appartenait a l'ancienne organisation
 * n'est donc jamais valide pour une autre, qu'elle soit active ou eteinte.
 * Changer reellement d'organisation exige donc toujours une nouvelle
 * selection.
 *
 * Le seul cas a preserver explicitement est le non-changement (le select
 * renvoie la meme organisation, ex. double-declenchement) : dans ce cas la
 * categorie courante doit rester telle quelle, meme si elle est eteinte et
 * donc absente de `newOrgCategories` (qui ne contient que les categories
 * actives chargees pour l'affichage/la saisie) — sinon une categorie
 * simplement eteinte serait remplacee en silence par `newOrgCategories[0]`
 * sans qu'aucun changement d'organisation reel ne le justifie.
 *
 * @param {Array<{id: string}>} newOrgCategories - categories actives chargees pour la nouvelle organisation
 * @param {string} previousOrgId - organization_id porte par l'entree avant le changement
 * @param {string} newOrgId - organization_id selectionne
 * @param {string} currentCategoryId - category_id porte par l'entree avant le changement
 * @returns {string} category_id a persister
 */
export function resolveCategoryOnOrgChange(newOrgCategories, previousOrgId, newOrgId, currentCategoryId) {
  if (newOrgId === previousOrgId) return currentCategoryId || '';
  const options = newOrgCategories || [];
  const stillValid = options.some(c => c.id === currentCategoryId);
  return stillValid ? currentCategoryId : (options[0]?.id || '');
}
